import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import ReCAPTCHA from 'react-google-recaptcha';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { CheckIcon } from '@heroicons/react/24/outline';

interface HeroData {
  title: string;
  subtitle: string;
  ctaText: string;
  backgroundImage: string;
  heroImage: string;
  enabled: boolean;
}

interface FeaturesSectionData {
  title: string;
  subtitle: string;
  enabled: boolean;
}

interface FeatureCard {
  id: string;
  title: string;
  description: string;
  iconName: string;
  iconColor: string;
  order: number;
  enabled: boolean;
}

interface HowItWorksSectionData {
  title: string;
  subtitle: string;
  videoUrl: string;
  videoCaption: string;
  enabled: boolean;
}

interface Step {
  id: string;
  number: number;
  title: string;
  description: string;
  order: number;
  enabled: boolean;
}

interface ContactContent {
  heading: string;
  subheading: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  messagePlaceholder: string;
  buttonText: string;
  successMessage: string;
  leftImageUrl: string;
  formEnabled: boolean;
}

interface Testimonial {
  id: string;
  userName: string;
  rating: number;
  reviewText: string;
  isFeatured: boolean;
  submittedAt: any;
}

interface PricingFeature {
  id: string;
  text: string;
  order: number;
}

interface PricingData {
  sectionTitle: string;
  sectionSubtitle: string;
  planTitle: string;
  displayPrice: string;
  priceNote: string;
  yearlyDisplayPrice?: string; // Optional yearly price
  yearlyPriceNote?: string; // Optional yearly note
  buttonText: string;
  leftImage: string;
  features: PricingFeature[];
  enabled: boolean;
}

const LandingPage: React.FC = () => {
  const [heroData, setHeroData] = useState<HeroData | null>(null);
  const [featuresSection, setFeaturesSection] =
    useState<FeaturesSectionData | null>(null);
  const [features, setFeatures] = useState<FeatureCard[]>([]);
  const [howItWorksSection, setHowItWorksSection] =
    useState<HowItWorksSectionData | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [contactContent, setContactContent] = useState<ContactContent | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  const recaptchaRef = useRef<any>(null);
  const navigate = useNavigate();

  // Load data from Firebase
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);

        // Load Hero Data
        const heroRef = doc(db, 'landingPageContent', 'hero');
        const heroSnap = await getDoc(heroRef);
        if (heroSnap.exists()) {
          setHeroData(heroSnap.data() as HeroData);
        }

        // Load Features Section
        const featuresSectionRef = doc(
          db,
          'landingPageContent',
          'featuresSection'
        );
        const featuresSectionSnap = await getDoc(featuresSectionRef);
        if (featuresSectionSnap.exists()) {
          setFeaturesSection(featuresSectionSnap.data() as FeaturesSectionData);
        }

        // Load Features
        const featuresRef = collection(
          db,
          'landingPageContent',
          'featuresSection',
          'features'
        );
        const featuresSnap = await getDocs(featuresRef);
        const featuresData: FeatureCard[] = [];
        featuresSnap.forEach((doc) => {
          const data = doc.data();
          if (data.enabled !== false) {
            featuresData.push({ id: doc.id, ...data } as FeatureCard);
          }
        });
        setFeatures(featuresData.sort((a, b) => a.order - b.order));

        // Load How It Works Section
        const howItWorksSectionRef = doc(
          db,
          'landingPageContent',
          'howItWorksSection'
        );
        const howItWorksSectionSnap = await getDoc(howItWorksSectionRef);
        if (howItWorksSectionSnap.exists()) {
          setHowItWorksSection(
            howItWorksSectionSnap.data() as HowItWorksSectionData
          );
        }

        // Load Steps
        const stepsRef = collection(
          db,
          'landingPageContent',
          'howItWorksSection',
          'steps'
        );
        const stepsSnap = await getDocs(stepsRef);
        const stepsData: Step[] = [];
        stepsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.enabled !== false) {
            stepsData.push({ id: doc.id, ...data } as Step);
          }
        });
        setSteps(stepsData.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

        // Load Pricing Section
        const pricingRef = doc(db, 'landingPageContent', 'pricing');
        const pricingSnap = await getDoc(pricingRef);
        if (pricingSnap.exists()) {
          setPricingData(pricingSnap.data() as PricingData);
        }

        // Load Contact Section
        const contactRef = doc(db, 'landingPageContent', 'contact');
        const contactSnap = await getDoc(contactRef);
        if (contactSnap.exists()) {
          setContactContent(contactSnap.data() as ContactContent);
        }

        // Load Featured Testimonials
        const testimonialsRef = collection(db, 'testimonials');
        const q = query(
          testimonialsRef,
          where('isFeatured', '==', true),
          orderBy('submittedAt', 'desc')
        );
        const testimonialsSnap = await getDocs(q);
        const testimonialsData: Testimonial[] = [];
        testimonialsSnap.forEach((doc) => {
          testimonialsData.push({ id: doc.id, ...doc.data() } as Testimonial);
        });
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    // Validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      setSubmitError('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }

    // Check if form is enabled
    if (contactContent?.formEnabled === false) {
      setSubmitError('Contact form is currently disabled');
      return;
    }

    // Get reCAPTCHA token
    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      setSubmitError('Please complete the reCAPTCHA verification');
      return;
    }

    setSubmitting(true);

    try {
      // Submit to Firebase
      await addDoc(collection(db, 'contactSubmissions'), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
        status: 'new',
        submittedAt: serverTimestamp(),
        recaptchaToken, // Store for potential verification
      });

      // Success!
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      recaptchaRef.current?.reset();

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Failed to submit form. Please try again later.');
      recaptchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    const icons: Record<string, JSX.Element> = {
      'shield-check': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
        />
      ),
      desktop: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      ),
      'chart-bar': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      ),
      clock: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      bell: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      ),
      lock: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      ),
      'lightning-bolt': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      ),
      shield: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      ),
      eye: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      ),
      sparkles: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
        />
      ),
      fire: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
        />
      ),
      star: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      ),
      heart: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      ),
      'check-circle': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      'light-bulb': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      ),
    };

    return icons[iconName] || icons['shield-check'];
  };

  // Helper function to get color classes
  const getColorClasses = (color: string) => {
    const colors: { [key: string]: { bg: string; icon: string } } = {
      blue: { bg: 'bg-blue-100', icon: 'text-blue-600' },
      pink: { bg: 'bg-pink-100', icon: 'text-pink-600' },
      green: { bg: 'bg-green-100', icon: 'text-green-600' },
      orange: { bg: 'bg-orange-100', icon: 'text-orange-600' },
      purple: { bg: 'bg-purple-100', icon: 'text-purple-600' },
      indigo: { bg: 'bg-indigo-100', icon: 'text-indigo-600' },
      red: { bg: 'bg-red-100', icon: 'text-red-600' },
      yellow: { bg: 'bg-yellow-100', icon: 'text-yellow-600' },
      teal: { bg: 'bg-teal-100', icon: 'text-teal-600' },
      gray: { bg: 'bg-gray-100', icon: 'text-gray-600' },
    };

    return colors[color] || colors['blue'];
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - clickable */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center hover:opacity-80 transition"
            >
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
              <span className="ml-2 text-xl font-bold text-gray-900">
                Unveil
              </span>
            </button>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              <a
                href="#features"
                className="text-gray-600 hover:text-blue-600 font-medium transition"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-gray-600 hover:text-blue-600 font-medium transition"
              >
                How It Works
              </a>
              <a
                href="#contact"
                className="text-gray-600 hover:text-blue-600 font-medium transition"
              >
                Contact Us
              </a>
            </nav>

            {/* Login Link */}
            <div>
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      {loading ? (
        <section className="relative py-20 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </section>
      ) : (
        <section
          className="relative py-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: heroData?.backgroundImage
              ? `url(${heroData.backgroundImage})`
              : 'linear-gradient(to bottom right, rgb(239 246 255), rgb(219 234 254))',
          }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left: Text Content */}
              <div>
                <h1 className="text-5xl font-bold leading-tight drop-shadow-lg">
                  {heroData?.title ? (
                    <div
                      className="text-white"
                      dangerouslySetInnerHTML={{ __html: heroData.title }}
                    />
                  ) : (
                    <span className="text-white">
                      Protect yourself from{' '}
                      <span className="text-blue-400">
                        deepfake audio scams
                      </span>
                    </span>
                  )}
                </h1>
                <div className="mt-6 text-lg text-white drop-shadow-md">
                  {heroData?.subtitle ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: heroData.subtitle }}
                    />
                  ) : (
                    <p>
                      Advanced AI detection of audio calls in real-time, keeping
                      you safe from sophisticated audio deepfake scams targeting
                      everyday phone users.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-8 bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-xl border-4 border-white hover:bg-blue-700 transition inline-flex items-center justify-center gap-4 shadow-lg"
                >
                  <svg
                    className="w-10 h-10"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  {heroData?.ctaText || 'Get Unveil Now'}
                </button>
              </div>

              {/* Right: Phone Mockup */}
              <div className="flex justify-center">
                <img
                  src={heroData?.heroImage || '/phone-mockup-1.png'}
                  alt="App Interface"
                  className="max-w-md w-full drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      {featuresSection?.enabled !== false && (
        <section id="features" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <h2 className="text-4xl font-bold text-gray-900">
              {featuresSection?.title ? (
                <div
                  dangerouslySetInnerHTML={{ __html: featuresSection.title }}
                />
              ) : (
                'Why Choose Us?'
              )}
            </h2>

            <div className="mt-4 text-lg text-gray-600">
              {featuresSection?.subtitle ? (
                <div
                  dangerouslySetInnerHTML={{ __html: featuresSection.subtitle }}
                />
              ) : (
                <p>
                  Advanced AI detection technology with features designed for
                  real-world protection.
                </p>
              )}
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              {features.length > 0 ? (
                features.map((feature) => {
                  const colors = getColorClasses(feature.iconColor);
                  return (
                    <div
                      key={feature.id}
                      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
                    >
                      <div
                        className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mb-4`}
                      >
                        <svg
                          className={`w-8 h-8 ${colors.icon}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {getIconComponent(feature.iconName)}
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        <div
                          dangerouslySetInnerHTML={{ __html: feature.title }}
                        />
                      </h3>
                      <div className="text-gray-600">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: feature.description,
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                // Fallback content if no features loaded
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Real-time Protection When It Matters Most
                  </h3>
                  <p className="text-gray-600">
                    Our platform analyzes calls live while you're speaking,
                    giving you instant alerts and stats.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      {howItWorksSection?.enabled !== false && (
        <section id="how-it-works" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900">
                {howItWorksSection?.title ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: howItWorksSection.title,
                    }}
                  />
                ) : (
                  'How it works'
                )}
              </h2>
              <div className="mt-4 text-lg text-gray-600">
                {howItWorksSection?.subtitle ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: howItWorksSection.subtitle,
                    }}
                  />
                ) : (
                  <p>
                    Three simple steps to stay protected from deepfake audio
                    scams
                  </p>
                )}
              </div>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-20">
              {steps.length > 0 ? (
                steps.map((step: Step) => (
                  <div key={step.id} className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-white text-2xl font-bold">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      <span dangerouslySetInnerHTML={{ __html: step.title }} />
                    </h3>
                    <div className="text-gray-600">
                      <div
                        dangerouslySetInnerHTML={{ __html: step.description }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                // Fallback content
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-white text-2xl font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Download & Install
                  </h3>
                  <p className="text-gray-600">
                    Download the app from the Google Play and complete the
                    simple setup process.
                  </p>
                </div>
              )}
            </div>

            {/* Video Demonstration */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl">
                {howItWorksSection?.videoUrl ? (
                  <video
                    src={howItWorksSection.videoUrl}
                    controls
                    className="w-full"
                    poster=""
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <button className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-8 transition-all transform hover:scale-110">
                      <svg
                        className="w-16 h-16 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  </div>
                )}
                <div className="bg-gray-900 py-4">
                  <p className="text-center text-gray-400 text-sm">
                    {howItWorksSection?.videoCaption ||
                      'Video demo and marketing video for our application'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <TestimonialsCarousel testimonials={testimonials} />

      {/* Pricing Section */}
      {pricingData?.enabled !== false && (
        <section id="pricing" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                <div
                  dangerouslySetInnerHTML={{
                    __html: pricingData?.sectionTitle || 'Our pricing',
                  }}
                />
              </h2>
              <div className="text-xl text-gray-600">
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      pricingData?.sectionSubtitle ||
                      'Simple, transparent pricing for everyone',
                  }}
                />
              </div>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  {/* Left - Phone Mockup */}
                  <div className="flex justify-center">
                    <img
                      src={pricingData?.leftImage || '/phone-mockup.png'}
                      alt="Unveil App"
                      className="w-full max-w-md"
                    />
                  </div>

                  {/* Right - Pricing Info */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            pricingData?.planTitle ||
                            'Our subscription offering',
                        }}
                      />
                    </h3>

                    <ul className="space-y-3 mb-8">
                      {pricingData?.features &&
                      pricingData.features.length > 0 ? (
                        pricingData.features
                          .sort((a, b) => a.order - b.order)
                          .map((feature) => (
                            <li key={feature.id} className="flex items-start">
                              <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                              <span>{feature.text}</span>
                            </li>
                          ))
                      ) : (
                        <>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>Get Access to our VoIP Caller</span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>
                              Call histories on all calls through Unveil App
                            </span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>
                              Call history and transparent call details summary
                            </span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>
                              Caller history retention for up to 30 days
                            </span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>Call analytics dashboard</span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>Priority customer support</span>
                          </li>
                          <li className="flex items-start">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span>Up to date security updates for our app</span>
                          </li>
                        </>
                      )}
                    </ul>

                    <div className="space-y-4 mb-6">
                      {/* Monthly Price */}
                      <div className="bg-blue-50 rounded-lg p-6">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">
                            Monthly Subscription
                          </span>
                          <div className="text-right">
                            <span className="text-3xl font-bold text-blue-600">
                              {pricingData?.displayPrice || 'SGD 3.00'}
                            </span>
                            <span className="text-gray-600 ml-2">
                              {pricingData?.priceNote || '/month'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Yearly Price (if available) */}
                      {pricingData?.yearlyDisplayPrice && (
                        <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-gray-600 font-medium block">
                                Yearly Subscription
                              </span>
                              <span className="text-sm text-green-600 font-semibold">
                                Save 17%
                              </span>
                            </div>
                            <div className="text-right">
                              <span className="text-3xl font-bold text-green-600">
                                {pricingData.yearlyDisplayPrice}
                              </span>
                              <span className="text-gray-600 ml-2">
                                {pricingData.yearlyPriceNote || '/year'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-blue-600 text-white py-4 px-8 rounded-lg font-semibold hover:bg-blue-700 transition text-lg"
                    >
                      {pricingData?.buttonText || 'Get Unveil Now'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              {contactContent?.heading || 'Contact Us'}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {contactContent?.subheading ||
                'Have questions or need help? Leave us a message!'}
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Phone Mockup */}
            <div className="flex justify-center">
              <img
                src={contactContent?.leftImageUrl || '/phone-mockup-2.png'}
                alt="App Screenshots"
                className="max-w-full w-full"
              />
            </div>

            {/* Right: Contact Form */}
            <div>
              {contactContent?.formEnabled !== false ? (
                <>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={submitting}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={
                          contactContent?.namePlaceholder || 'Your name'
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={submitting}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={
                          contactContent?.emailPlaceholder ||
                          'your.email@example.com'
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={submitting}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={
                          contactContent?.messagePlaceholder ||
                          'Your message...'
                        }
                      />
                    </div>

                    {/* reCAPTCHA */}
                    <div className="flex justify-center">
                      {React.createElement(ReCAPTCHA as any, {
                        ref: recaptchaRef,
                        sitekey: import.meta.env
                          .VITE_RECAPTCHA_SITE_KEY_CONTACTFORM,
                        size: 'normal',
                      })}
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <div className="p-4 bg-red-100 text-red-700 rounded-lg text-sm">
                        {submitError}
                      </div>
                    )}

                    {/* Success Message */}
                    {submitSuccess && (
                      <div className="p-4 bg-green-100 text-green-700 rounded-lg">
                        {contactContent?.successMessage ||
                          "Thank you! We'll get back to you soon."}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting
                        ? 'Sending...'
                        : contactContent?.buttonText || 'Send'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">
                    Contact form is currently disabled.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-20 bg-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Protect Yourself?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Download our app today and start staying safe from deepfake audio
            scams.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="bg-blue-600 text-white px-12 py-5 rounded-xl font-bold text-2xl border-4 border-white hover:bg-blue-700 transition inline-flex items-center justify-center gap-4 shadow-lg"
          >
            <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            Get Unveil Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <svg
                  className="w-8 h-8 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                  />
                </svg>
                <span className="ml-2 text-xl font-bold text-white">
                  Unveil
                </span>
              </div>
              <p className="text-sm">
                Protecting you from deepfake audio scams with advanced AI
                technology.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#features" className="hover:text-white transition">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition">
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#how-it-works"
                    className="hover:text-white transition"
                  >
                    How it Works
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; 2026 Unveil. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
